import cx from "classnames";
import { JSX, ReactNode, useEffect, useId, useRef, useState } from "react";
import { QESButton, QESCheckbox, QESTextInput } from "../qes-standards";
import LoadingSpinner from "../LoadingSpinner";

import { DropdownItem } from "./types";

interface MultiselectDropdownProps<T> {
  /**
   * Option to autofocus the search text box on open
   */
  autofocusSearch?: boolean;
  /**
   * Optional class name(s) added to the div outside the dropdown
   */
  containerClassName?: string;
  /**
   * A string to be rendered next to the Select All button.<br/>
   * This defaults to "Select All"
   */
  customSelectAllText?: string;
  /**
   * A string to be rendered inside the text input/dropdown button.<br/>
   * This defaults to "x item(s) selected"
   */
  customSelectInputText?: string;
  customWidth?: boolean;
  /**
   * Disables the text field, dropdown button, and all inputs and checkboxes inside the dropdown
   */
  disabled?: boolean;
  /**
   * Visually hides the label above the dropdown
   */
  hideLabel?: boolean;
  /**
   * Hides the search field within the dropdown
   */
  hideSearch?: boolean;
  /**
   * Hides the bulk "select all" checkbox within the dropdown
   */
  hideSelectAll?: boolean;
  /**
   * The list of items rendered within the dropdown
   */
  items: Array<DropdownItem<T>>;
  /**
   * The label of the text element outside the dropdown
   */
  label: string | JSX.Element;
  /**
   * The label of the text element outside the dropdown
   */
  onSearchTextChange?: (search: string) => void
  /**
   * A function called on every checkbox change, returning an array of the items currently selected
   */
  onSelectionChanged?: (tempSelectedItems: Array<DropdownItem<T>>) => void;
  /**
   * An array of the items currently selected, to be maintained by a parent component
   */
  selectedItems: Array<DropdownItem<T>>;
  /**
   * An optional string that appears in a tooltip to the right of the label.<br/>
   * This is intended to display validation requirements to the user.
   */
  validationTooltipContent?: string | JSX.Element;
  /**
   * Required, static, unique string to help QA Automation run tests involving this component
   */
  testId: string;
  required?: boolean;
  isValid?: boolean;
  /**
   * The alias to refer to the selected items. This will override the default value of "item(s) selected", provide appropriate plural extension for overridden alias.  
   */
  itemAlias?: string;
  /**
   * Display label text in tooltip if text overflows container.
   */
  showTooltipOnOverflow?: boolean;
  /**
  * Display the first item text plus a count of the selected items in the selectInputText string
  * instead of the item(s) selected count.
  */
  showSelectedTextNotItemsSelectedCount?: boolean;

  /**
   * Displayed in the case of an empty list.
   */
  noItemsDescription?: string;
  /**
   * Displayed when the (non-empty) list is filtered to no results
   */
  noVisibleItemsDescription?: string;
  /**
   * Display a spinner when true
   */
  isLoading?: boolean;
}

/**
 * A button that summons a dropdown. The dropdown includes
 * a search field for its contents, a bulk select all checkbox, and a line item 
 * for each item passed in through the items prop. A change event handler is called on 
 * every checkbox click, so there's no need for a submit button. <br /><br />
 * Storybook note: The dotted rectangle is the example's document body, or the area 
 * a user can click in to hide the dropdown.
 */
const MultiselectDropdown = <T,>(props: MultiselectDropdownProps<T> & { children?: ReactNode }): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  const [tempSelectedItems, setTempSelectedItems] = useState(props.selectedItems);
  const [selectInputText, setSelectInputText] = useState("")
  const [searchText, setSearchText] = useState("");
  const [visibleItems, setVisibleItems] = useState(props.items);
  const [selectAll, setSelectAll] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const uniqueId = useId();

  useEffect(() => {
    updateSearchText("");
  }, [isVisible]);

  useEffect(() => {
    setTempSelectedItems(props.selectedItems);
  }, [isVisible, props.selectedItems]);

  useEffect(() => {
    setSelectAll(tempSelectedItems.length === props.items.length || visibleItems.every(v => tempSelectedItems.map(i => i.key).includes(v.key)));
    if (!props.customSelectInputText) {
      if (tempSelectedItems.length > 0) {
        if (props.showSelectedTextNotItemsSelectedCount === true) {
          let textToDisplay = tempSelectedItems[0].name;
          if (tempSelectedItems.length > 1) {
            textToDisplay = textToDisplay + " - " + `${tempSelectedItems.length} ${props.itemAlias || 'item(s) selected'}`;
          }
          setSelectInputText(`${props.itemAlias || textToDisplay}`);
        } else {
          setSelectInputText(`${tempSelectedItems.length} ${props.itemAlias || "item(s) selected"}`);
        }
      } else {
        setSelectInputText("");
      }
    }
  }, [tempSelectedItems, visibleItems, props.items]);

  useEffect(() => {
    setVisibleItems(props.items.filter(x => x.name.toLowerCase().includes(searchText.toLowerCase())));
  }, [props.items, searchText]);

  useEffect(() => {
    if (isVisible) {
      const handleExternalClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest(`#${CSS.escape(uniqueId)}.multiselect-dropdown-container`) === null && isVisible) {
          setIsVisible(false);
          document.body.removeEventListener("click", handleExternalClick);
        }
      };
      document.body.addEventListener("click", handleExternalClick);

      return () => {
        document.body.removeEventListener("click", handleExternalClick);
      };
    }
  }, [isVisible]);

  const updateSearchText = (text: string) => {
    if (props.onSearchTextChange) {
      props.onSearchTextChange(text);
    }
    setSearchText(text);
  };

  const updateTempSelectedItems = (key: T) => {
    const item = props.items.find((i) => i.key === key);
    const isChecked = tempSelectedItems.find((tsi) => tsi.key === item.key);
    let newSelectedItems = tempSelectedItems.map((tsi) => tsi);
    if (isChecked) {
      newSelectedItems = newSelectedItems.filter((i) => i.key !== item.key);
    } else {
      newSelectedItems.push(item);
    }
    setTempSelectedItems(newSelectedItems);

    if (props.onSelectionChanged)
      props.onSelectionChanged(newSelectedItems);
  };

  const toggleSelectAll = () => {
    let newSelectedItems;

    if (selectAll) {
      newSelectedItems = tempSelectedItems.filter(i => !visibleItems.map(v => v.key).includes(i.key));
    }
    else {
      newSelectedItems = tempSelectedItems.length === 0 ? visibleItems : visibleItems.concat(tempSelectedItems.filter(i => !visibleItems.map(t => t.key).includes(i.key)));
    }

    setTempSelectedItems(newSelectedItems);
    if (props.onSelectionChanged)
      props.onSelectionChanged(newSelectedItems);
  }

  const getIndeterminate = () => {
    return visibleItems.length > 0
      && tempSelectedItems.length > 0
      && visibleItems.some(v => tempSelectedItems.map(i => i.key).includes(v.key))
      && !visibleItems.every(v => tempSelectedItems.map(i => i.key).includes(v.key))
  }

  const shouldShowNoItemsDescription = props.noItemsDescription != null && !props.isLoading && props.items.length == 0;
  const shouldShowNoVisibleItemsDescription = props.noVisibleItemsDescription != null && !props.isLoading && props.items.length != 0 && visibleItems.length == 0;

  return (
    <div className={cx({
      [props.containerClassName]: !!props.containerClassName,
      "multiselect-dropdown-container": true,
    })}
      id={uniqueId}
    >
      <QESTextInput
        containerClassName="select-input"
        disabled={props.disabled}
        hideLabel={props.hideLabel} 
        qesRef={inputRef}
        required={props.required}
        isValid={props.isValid}
        inputAppend={
          <QESButton
            qesType="secondary"
            disabled={props.disabled}
            qesTestId="access-reports-admin__add-specialties--input-add"
            onMouseDown={(e) => { e.preventDefault(); inputRef.current.focus(); inputRef.current.click(); }}
            className="select-button"
            aria-label="Toggle Dropdown"
          >
            <i className="fa fa-chevron-down text-dark" />
          </QESButton>
        }

        label={props.label}
        onClick={() => setIsVisible(!isVisible)}
        readOnly={true}
        testId={`search-checkbox-dropdown__${props.testId}-select-input`}
        validationTooltipContent={props.validationTooltipContent}
        value={!!props.customSelectInputText ? props.customSelectInputText : selectInputText}
      />
      {isVisible && (
        <div
          className={cx({
            "checkbox-dropdown": true,
            "w-100": !props.customWidth,
          })}
        >
          {props.isLoading &&
            <div className="checkbox-dropdown__label text-center">
              <LoadingSpinner />
            </div>
          }
          {shouldShowNoItemsDescription &&
            <p className="checkbox-dropdown__label text-wrap">
              {props.noItemsDescription}
            </p>
          }
          {!props.isLoading && !shouldShowNoItemsDescription && (
            <>
              {!props.hideSearch ?
                <QESTextInput
                  testId={`search-checkbox-dropdown__${props.testId}-search-input`}
                  disabled={props.disabled}
                  hideLabel={true}
                  label=""
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => updateSearchText(e.target.value)}
                  containerClassName="checkbox-dropdown__label"
                  autoFocus={props.autofocusSearch}
                /> : null
              }
              {!props.hideSelectAll &&
                <QESCheckbox
                  checked={selectAll}
                  className="checkbox-dropdown__checkbox"
                  containerClassName="checkbox-dropdown__label"
                  disabled={props.disabled}
                  indeterminate={getIndeterminate()}
                  label={!!props.customSelectAllText ? props.customSelectAllText : "Select All"}
                  onChange={() => toggleSelectAll()}
                  testId={`search-checkbox-dropdown__${props.testId}-select-all`}
                />
              }
              {shouldShowNoVisibleItemsDescription &&
                <p className="checkbox-dropdown__label text-wrap">
                  {props.noVisibleItemsDescription}
                </p>
              }
              {visibleItems.map((item, index) => {
                if (!item) {
                  return;
                }

                const isSelected = !!tempSelectedItems.find((tsi) => tsi.key === item.key);

                return (
                  <div key={index}>
                    <QESCheckbox
                      containerClassName="checkbox-dropdown__label"
                      label={item.name}
                      checked={isSelected}
                      className="checkbox-dropdown__checkbox"
                      disabled={props.disabled}
                      onChange={() => updateTempSelectedItems(item.key)}
                      onLabelClick={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      testId={`search-checkbox-dropdown__${props.testId}-checkbox-${index}`}
                      showTooltipOnOverflow={props.showTooltipOnOverflow}
                    />
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiselectDropdown;
export { DropdownItem };
